import { useFocusEffect, type Href } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import * as React from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { androidStatusBarHeight } from '@/components/android-status-bar-band';
import { ProfileAffiliations } from '@/components/profiles/profile-affiliations';
import { ProfileScreen } from '@/components/profiles/profile-screen';
import { ProfileTopBar } from '@/components/profiles/profile-top-bar';
import { MemberQrControls } from '@/components/qr/member-qr-controls';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { acceptFriendRequest, fetchFriends, type FriendListItem } from '@/lib/friends';
import { type Profile } from '@/lib/member-profile';
import { BRAND } from '@sixam/config/brand';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

function displayNameFor(profile: Profile | null, email?: string | null) {
  return profile?.full_name || email || '6am member';
}

function friendStatusLabel(item: FriendListItem) {
  if (item.friendship.status === 'accepted') {
    return 'Friend';
  }
  return item.direction === 'inbound' ? 'Wants to connect' : 'Request sent';
}

function matchesProfileSearch(query: string, values: (string | null | undefined)[]) {
  if (!query) {
    return true;
  }
  return values.some((value) => value?.toLowerCase().includes(query));
}

// This screen intentionally coordinates profile, membership, QR, and optional friend states.
// eslint-disable-next-line sonarjs/cognitive-complexity
export default function MeScreen() {
  const { session, profile } = useSession();
  const { activeTenant, memberships, setActiveTenantSlug, tenants } = useTenant();
  const insets = useSafeAreaInsets();
  // The app root already reserves the status bar strip and hands descendants a
  // zeroed top inset, but this screen sits under a native search header that the
  // OS still positions below the status bar. Add the real status bar height back
  // so the avatar and the QR buttons clear that header instead of hiding under
  // it. Android only; iOS keeps a real top inset already.
  const headerTopOffset = insets.top + androidStatusBarHeight();
  const userId = session?.user.id;
  const userEmail = session?.user.email;
  const friendConnectionsEnabled = useReleaseFeatureFlag('friendConnections') === true;

  const [isQrOpen, setIsQrOpen] = React.useState(false);
  const [isScannerOpen, setIsScannerOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [friends, setFriends] = React.useState<FriendListItem[]>([]);
  const [isLoadingFriends, setIsLoadingFriends] = React.useState(false);
  const [profileSearchQuery, setProfileSearchQuery] = React.useState('');

  useFocusEffect(
    React.useCallback(() => {
      setActiveTenantSlug(null);
    }, [setActiveTenantSlug])
  );

  React.useEffect(() => {
    let active = true;

    async function loadFriends() {
      setIsLoadingFriends(true);
      try {
        const data = await fetchFriends();
        if (active) {
          setFriends(data);
        }
      } catch {
        if (active) {
          setFriends([]);
        }
      } finally {
        if (active) {
          setIsLoadingFriends(false);
        }
      }
    }

    if (userId && friendConnectionsEnabled) {
      void loadFriends();
    }

    return () => {
      active = false;
    };
  }, [friendConnectionsEnabled, userId]);

  const currentProfile = profile;
  const bio = currentProfile?.bio ?? '';
  const displayName = displayNameFor(currentProfile, userEmail);
  const initials = displayName.slice(0, 2).toUpperCase();
  const memberClubs = React.useMemo(() => {
    const tenantById = new Map(tenants.map((tenant) => [tenant.id, tenant]));
    return memberships
      .map((membership) => {
        const tenant = tenantById.get(membership.tenant_id);
        if (!tenant) {
          return null;
        }
        const name = tenant.name;
        return {
          avatarUrl: tenant.avatar_url,
          href: {
            pathname: '/app/clubs/[tenant]',
            params: { tenant: tenant.slug },
          } as Href,
          id: tenant.id,
          initials: name.slice(0, 2).toUpperCase(),
          name,
          subtitle: membership.role,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [memberships, tenants]);
  const normalizedProfileSearchQuery = profileSearchQuery.trim().toLowerCase();
  const visibleMemberClubs = React.useMemo(
    () =>
      memberClubs.filter((club) =>
        matchesProfileSearch(normalizedProfileSearchQuery, [club.name, club.subtitle])
      ),
    [memberClubs, normalizedProfileSearchQuery]
  );
  const visibleFriends = React.useMemo(
    () =>
      friends.filter((item) =>
        matchesProfileSearch(normalizedProfileSearchQuery, [
          item.friend.fullName,
          friendStatusLabel(item),
        ])
      ),
    [friends, normalizedProfileSearchQuery]
  );

  async function acceptFriend(friendshipId: string) {
    setError(null);
    try {
      await acceptFriendRequest(friendshipId);
      setFriends((current) =>
        current.map((item) =>
          item.friendship.id === friendshipId
            ? { ...item, friendship: { ...item.friendship, status: 'accepted' } }
            : item
        )
      );
    } catch (friendError) {
      setError(friendError instanceof Error ? friendError.message : 'Could not accept request.');
    }
  }

  return (
    <View className="flex-1 bg-background" collapsable={false}>
      <StatusBar style="light" />
      <View
        className="absolute left-0 right-0 top-0"
        pointerEvents="none"
        style={{ backgroundColor: BRAND.color.ink, height: headerTopOffset + 72 }}
      />
      {Platform.OS !== 'web' ? (
        <ProfileTopBar
          kind="member"
          onAddPress={friendConnectionsEnabled ? () => setIsScannerOpen(true) : undefined}
          onOpenMemberQr={() => setIsQrOpen(true)}
          onSearchChange={setProfileSearchQuery}
          searchPlaceholder="Search profile"
        />
      ) : null}

      <ProfileScreen
        avatarAlt={`${displayName}'s avatar`}
        avatarUrl={currentProfile?.avatar_url}
        bio={bio || 'Morning miles, Bangkok heat, post-run coffee.'}
        contentInsetAdjustmentBehavior="never"
        displayName={displayName}
        email={userEmail}
        error={error}
        initials={initials}
        stats={[
          { label: 'Runs', value: '42' },
          { label: 'Friends', value: String(friends.length) },
          { compact: true, label: 'Role', value: activeTenant ? 'Member' : 'Runner' },
        ]}
        topPadding={headerTopOffset + 52}
      >
        <ProfileAffiliations
          description="Clubs where this account has an active membership."
          emptyText={
            normalizedProfileSearchQuery
              ? 'No clubs match this search.'
              : 'No club memberships yet.'
          }
          items={visibleMemberClubs}
          modalTitle="My clubs"
          title="My clubs"
        />

        {friendConnectionsEnabled ? (
          <Card>
            <CardHeader>
              <CardTitle>Friends</CardTitle>
              <CardDescription>Runners you have connected with from QR scans.</CardDescription>
            </CardHeader>
            <CardContent className="gap-3">
              {isLoadingFriends ? <ActivityIndicator /> : null}
              {!isLoadingFriends && friends.length === 0 ? (
                <Text variant="muted">No friends yet. Scan a QR after a run to connect.</Text>
              ) : null}
              {!isLoadingFriends &&
              friends.length > 0 &&
              normalizedProfileSearchQuery &&
              visibleFriends.length === 0 ? (
                <Text variant="muted">No friends match this search.</Text>
              ) : null}
              {visibleFriends.map((item) => (
                <View
                  key={item.friendship.id}
                  className="gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <View className="min-w-0 flex-1">
                    <Text variant="large">{item.friend.fullName ?? '6am member'}</Text>
                    <Text variant="muted">{friendStatusLabel(item)}</Text>
                  </View>
                  {item.friendship.status === 'pending' && item.direction === 'inbound' ? (
                    <Button size="sm" onPress={() => void acceptFriend(item.friendship.id)}>
                      <Text>Accept</Text>
                    </Button>
                  ) : null}
                </View>
              ))}
            </CardContent>
          </Card>
        ) : null}
      </ProfileScreen>

      {/*
        Rendered last, above ProfileScreen, so the profile's own dark header does
        not paint over the floating buttons. The QR/scan controls are shared with
        the club profile so both scopes behave the same.
      */}
      <MemberQrControls
        onQrOpenChange={setIsQrOpen}
        onScannerOpenChange={setIsScannerOpen}
        qrOpen={isQrOpen}
        scannerOpen={isScannerOpen}
        scannerEnabled={friendConnectionsEnabled}
        tenantSlug={activeTenant?.slug}
        topOffset={headerTopOffset + 80}
      />
    </View>
  );
}
